import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  Home,
  Building,
  Package,
  Users,
  Briefcase,
  Bell,
  Settings,
  Menu,
  X,
  User,
  FileText,
  Receipt,
  Calculator,
  DollarSign,
  ClipboardList,
  UserPlus,
  FilePlus,
  Store,
  FileText as FileTextIcon,
  Shield,
  FolderOpen,
  FolderKanban,
  Layers,
  ShoppingBag,
  LockKeyhole
} from "lucide-react";
import styles from "./BotoneraModern.module.scss";
import BotonModern from "./BotonModern.tsx";
import LogOutMenu from "../Perfil/LogOutMenu/LogOutMenu";
import useUserImage from "../../hooks/useUserImage";
import logoNavbar from "../../assets/images/Logo-edify-no-bg.png";
import { buildProfileUrl, getProfileTabFromQuery, PROFILE_SUBITEM_TAB_MAPPING, PROFILE_TAB_TO_NAV_ITEM, getProfilePathInfo, getUserDisplayName } from "../../utils/navigationHelpers.ts";

interface BotoneraModernProps {
  mobileStylesFromParent?: boolean;
  userData: any;
  newNotifications: number;
  profileVisualizerData?: any;
}

interface NavSubItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  route: string;
  directRoute?: string;
}

interface NavButton {
  id: string;
  nombre: string;
  icono: React.ReactNode;
  color: string;
  route: string;
  badge?: number;
  adminOnly?: boolean;
  hasSubmenu?: boolean;
  subItems?: NavSubItem[];
}

const BotoneraModern: React.FC<BotoneraModernProps> = ({ 
  mobileStylesFromParent, 
  userData,
  newNotifications,
  profileVisualizerData
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState<string>("");
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const profilePathInfo = useMemo(() => getProfilePathInfo(location.pathname), [location.pathname]);

  const { imageError, initials, profileImage } = useUserImage(userData);
  const isUserAdmin = userData?.rols && userData.rols.filter((rol: any) => rol.name === "ADMIN").length > 0;

  const navButtons: NavButton[] = [
    {
      id: "dashboard",
      nombre: "Inicio",
      icono: <Home size={20} />,
      color: "#7000ff",
      route: "/dashboard"
    },
    {
      id: "obras",
      nombre: "Obras",
      icono: <Building size={20} />,
      color: "#234ba2",
      route: "/obrasprivadas",
      hasSubmenu: true,
      subItems: [
        {
          id: "presupuestos-obra",
          label: "Presupuestos para mi obra",
          icon: <FileTextIcon size={16} />,
          route: "/obrasprivadas/crear",
          directRoute: "/obrasprivadas/crear"
        },
        {
          id: "cotizar-obras",
          label: "Cotizar en obras",
          icon: <Receipt size={16} />,
          route: "/obrasprivadas/explorar",
          directRoute: "/obrasprivadas/explorar"
        },
        {
          id: "obras-publicas",
          label: "Obras públicas",
          icon: <Building size={16} />,
          route: "/obraspublicas",
          directRoute: "/obraspublicas"
        },
        {
          id: "mis-proyectos",
          label: "Mis proyectos en Edify",
          icon: <FolderOpen size={16} />,
          route: "#",
          directRoute: "#"
        }
      ]
    },
    {
      id: "materiales",
      nombre: "Materiales",
      icono: <Package size={20} />,
      color: "#45a7ff",
      route: "/cotizaciondemateriales",
      hasSubmenu: true,
      subItems: [
        {
          id: "estimacion-costos",
          label: "Estimación de costos",
          icon: <Calculator size={16} />,
          route: "/cotizaciondemateriales/estimacion-costos",
          directRoute: "/cotizaciondemateriales/estimacion-costos"
        },
        {
          id: "cotizar-materiales",
          label: "Pedir una cotización",
          icon: <DollarSign size={16} />,
          route: "/cotizaciondemateriales/crear",
          directRoute: "/cotizaciondemateriales/crear"
        },
        {
          id: "cotizaciones-materiales",
          label: "Explorar cotizaciones",
          icon: <ClipboardList size={16} />,
          route: "/cotizaciondemateriales/explorar",
          directRoute: "/cotizaciondemateriales/explorar"
        },
        {
          id: "mis-cotizaciones-materiales",
          label: "Mis cotizaciones de materiales",
          icon: <FolderKanban size={16} />,
          route: "#",
          directRoute: "#"
        }
      ]
    },
    {
      id: "personas",
      nombre: "Personas",
      icono: <Users size={20} />,
      color: "#1a3a7a",
      route: "/buscar-perfiles",
      hasSubmenu: true,
      subItems: [
        {
          id: "encontrar-profesionales",
          label: "Explorar perfiles",
          icon: <Users size={16} />,
          route: "/buscar-perfiles",
          directRoute: "/buscar-perfiles"
        },
        {
          id: "publicar-oferta-trabajo",
          label: "Publicar oferta de trabajo",
          icon: <FilePlus size={16} />,
          route: "/mercado-de-trabajo/crearoferta",
          directRoute: "/mercado-de-trabajo/crearoferta"
        },
        {
          id: "postularme-ofertas",
          label: "Postularme a ofertas de trabajo",
          icon: <UserPlus size={16} />,
          route: "/bolsa-trabajo",
          directRoute: "/bolsa-trabajo"
        },
        {
          id: "mis-postulaciones",
          label: "Mis postulaciones",
          icon: <LockKeyhole size={16} />,
          route: "#",
          directRoute: "#"
        }
      ]
    },
    {
      id: "marketplace",
      nombre: "Marketplace",
      icono: <Store size={20} />,
      color: "#7000ff",
      route: "/marketplace",
      hasSubmenu: true,
      subItems: [
        {
          id: "explorar-servicios",
          label: "Explorar servicios/productos",
          icon: <Store size={16} />,
          route: "/marketplace/explorar",
          directRoute: "/marketplace/explorar"
        },
        {
          id: "ofrecer-servicios",
          label: "Ofrecer servicios/productos",
          icon: <Package size={16} />,
          route: "#",
          directRoute: "#"
        },
        {
          id: "mis-productos-servicios",
          label: "Mis productos/servicios",
          icon: <ShoppingBag size={16} />,
          route: "#",
          directRoute: "#"
        }
      ]
    },
    /* {
      id: "documentacion",
      nombre: "Documentación",
      icono: <FileText size={20} />,
      color: "#059669",
      route: "/documentacion-de-obra",
      hasSubmenu: true,
      subItems: [
        {
          id: "comercializar-documentacion",
          label: "Comercializar documentación",
          icon: <FileText size={16} />,
          route: "/documentacion-de-obra/crear",
          directRoute: "/documentacion-de-obra/crear"
        },
        {
          id: "mis-documentaciones",
          label: "Mis documentaciones de obra",
          icon: <Layers size={16} />,
          route: "#",
          directRoute: "#"
        }
      ]
    }, */
    {
      id: "notificaciones",
      nombre: "Notificaciones",
      icono: <Bell size={20} />,
      color: "#D05004",
      route: "/notificaciones",
      badge: newNotifications
    },
    {
      id: "admin",
      nombre: "Admin",
      icono: <Shield size={20} />,
      color: "#dc2626",
      route: "/backoffice",
      adminOnly: true,
      hasSubmenu: true,
      subItems: [
        {
          id: "backoffice",
          label: "Backoffice",
          icon: <Settings size={16} />,
          route: "/backoffice",
          directRoute: "/backoffice"
        }
      ]
    }
  ];

  const filteredButtons = navButtons.filter(button => 
    !button.adminOnly || isUserAdmin
  );

  useEffect(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const currentTab = getProfileTabFromQuery(searchParams);
    if (pathname === "/dashboard") {
      setActiveRoute("dashboard");
      return;
    }
    if (pathname.startsWith("/profesionales")) {
      if (userData) {
        const segments = pathname.split("/").filter(Boolean);
        if (segments[1] === String(userData.id) && currentTab && PROFILE_TAB_TO_NAV_ITEM[currentTab]) {
          setActiveRoute("");
          return;
        }
        setActiveRoute("");
        return;
      }
      setActiveRoute("");
      return;
    }
    if (pathname.startsWith("/obrasprivadas") || pathname.startsWith("/obraspublicas")) {
      setActiveRoute("obras");
    } else if (pathname.startsWith("/cotizaciondemateriales")) {
      setActiveRoute("materiales");
    } else if (pathname.startsWith("/mercado-de-trabajo") || pathname.startsWith("/bolsa-trabajo")) {
      setActiveRoute("personas");
    } else if (pathname.startsWith("/marketplace")) {
      setActiveRoute("marketplace");
    } else if (pathname.startsWith("/documentacion-de-obra")) {
      setActiveRoute("documentacion");
    } else if (pathname.startsWith("/notificaciones")) {
      setActiveRoute("notificaciones");
    } else if (pathname.startsWith("/backoffice")) {
      setActiveRoute("admin");
    } else {
      setActiveRoute("");
    }
  }, [location.pathname, location.search, userData]);

  const handleButtonClick = (button: NavButton) => {
    if (button.hasSubmenu && button.subItems) {
      // Toggle submenu
      setExpandedSubmenus(prev => {
        const newSet = new Set(prev);
        if (newSet.has(button.id)) {
          newSet.delete(button.id);
        } else {
          // Close all other submenus first
          newSet.clear();
          newSet.add(button.id);
        }
        return newSet;
      });
    } else {
      // Navigate directly
      navigate(button.route);
      setIsMenuOpen(false);
    }
  };

  const handleSubItemClick = (subItem: NavSubItem) => {
    if (subItem.id === "ofrecer-servicios") {
      navigate("/dashboard");
      setIsMenuOpen(false);
      setExpandedSubmenus(new Set());
      return;
    }
    const profileTab = PROFILE_SUBITEM_TAB_MAPPING[subItem.id];
    if (profileTab) {
      const { isProfile, userId: pathUserId, userName: pathUserName } = profilePathInfo;
      const ownUserId = userData?.id ? String(userData.id) : null;
      const ownDisplayName = getUserDisplayName(userData);
      const candidateProfileData = (() => {
        if (!isProfile) return userData;
        if (ownUserId && pathUserId === ownUserId) return userData;
        if (profileVisualizerData && profileVisualizerData.id && String(profileVisualizerData.id) === pathUserId) {
          return profileVisualizerData;
        }
        return profileVisualizerData;
      })();
      const candidateId = candidateProfileData?.id ? String(candidateProfileData.id) : (isProfile ? pathUserId : ownUserId);
      const candidateName = getUserDisplayName(candidateProfileData) || (isProfile ? pathUserName : null) || ownDisplayName || pathUserName || 'perfil';
      if (candidateId && candidateName) {
        const profileUrl = buildProfileUrl(candidateId, candidateName, profileTab);
        navigate(profileUrl);
        setIsMenuOpen(false);
        setExpandedSubmenus(new Set());
        return;
      }
      setIsMenuOpen(false);
      setExpandedSubmenus(new Set());
      return;
    }
    navigate(subItem.route);
    setIsMenuOpen(false);
    setExpandedSubmenus(new Set());
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
  };

  const handleProfileClick = () => {
    navigate(`/perfil/${userData.id}`);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };


  return (
    <nav className={styles.container}>
      {/* Header fijo */}
      <div className={styles.header}>
        <div className={styles.logo}>
          <img 
            src={logoNavbar} 
            alt="Edify" 
            className={styles.logoImage}
          />
        </div>

        <button 
          className={styles.menuButton} 
          onClick={toggleMenu}
          aria-label="Abrir menú"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className={styles.overlay} 
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Menú deslizable */}
      {isMenuOpen && (
        <div className={styles.menuContainer}>
          {/* Perfil del usuario */}
          <div className={styles.profileSection} onClick={handleProfileClick}>
            <div className={styles.profileImage}>
              {!imageError && profileImage ? (
                <img
                  src={profileImage}
                  alt="Perfil"
                  className={styles.profileImg}
                />
              ) : initials ? (
                <div className={styles.profileInitials}>{initials}</div>
              ) : (
                <div className={styles.profileDefault}>
                  <User size={24} />
                </div>
              )}
            </div>
            <div className={styles.profileInfo}>
              <h3 className={styles.profileName}>
                {userData?.type === "COMPANY" 
                  ? userData?.razon_social 
                  : `${userData?.name} ${userData?.last_name}`}
              </h3>
              <p className={styles.profileEmail}>{userData?.username}</p>
            </div>
          </div>

          {/* Botones de navegación */}
          <div className={styles.navigationSection}>
            {filteredButtons.map((button) => (
              <div key={button.id} className={styles.navItemContainer}>
                <BotonModern
                  button={button}
                  isActive={activeRoute === button.id}
                  onClick={() => handleButtonClick(button)}
                  hasSubmenu={button.hasSubmenu}
                  isSubmenuExpanded={expandedSubmenus.has(button.id)}
                />
                
                  {/* Submenú */}
                  {button.hasSubmenu && button.subItems && expandedSubmenus.has(button.id) && (
                    <div className={styles.submenuContainer}>
                      {button.subItems.map((subItem) => (
                        <button
                          key={subItem.id}
                          className={styles.submenuItem}
                          onClick={() => handleSubItemClick(subItem)}
                        >
                          <div className={styles.submenuIcon}>
                            {subItem.icon}
                          </div>
                          <span className={styles.submenuLabel}>{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
            ))}
          </div>

          {/* Sección de logout */}
          <div className={styles.logoutSection} onClick={handleLogoutClick}>
            <LogOutMenu />
          </div>
        </div>
      )}

    </nav>
  );
};

const mapStateToProps = (state: any) => ({
  userData: state.general.userData,
  profileVisualizerData: state.general.profileVisualizer,
  newNotifications: state.dashboard.newNotifications,
});

export default connect(mapStateToProps)(BotoneraModern);
